import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../utils/factoryUtils";
import {
  AplicacionItem,
  AplicacionService,
} from "../../../generated/grpc/aplicaciones_pb";
import { MetodoAplicacion } from "../response/AplicacionResponse";
import { grpcAplicacionDatabase } from "../database/GrpcAplicacionDatabase";
import { serviceSync } from "../../../base/services/ServiceSync";
import { AplicacionResponse } from "../response/AplicacionResponse";

/** Variante gRPC-Web (con delta real, por finca) de `AplicacionGetListByFincaIdService`
 *  (ver ese archivo). Devuelve `AplicacionResponse[]` -mismo shape que ya usa
 *  el resto de la app (`AplicacionGetYearGroupListByFincaIdService`,
 *  `monitoreoAnalisisProyeccion.ts`, etc.)-, así que el swap queda contenido
 *  acá. Rellena con valores inertes los campos que se comprobó que nadie lee
 *  (`clienteId`, `cultivoId`, `cultivoTratamientoId`, `descargaHa` — ver el
 *  comentario de `AplicacionItem` en el proto). */
class AplicacionGrpcService {
  constructor(private readonly _fincaId: string) {}

  /** Sincroniza (si el cooldown lo permite) y devuelve el listado completo
   *  de la finca desde IndexedDB, ya en el shape REST. */
  async getListAsync(): Promise<AplicacionResponse[]> {
    await this._syncAsync();
    const items = await grpcAplicacionDatabase.getListByFincaIdAsync<AplicacionItem>(
      this._fincaId,
    );
    return items.map(toAplicacionResponse);
  }

  /** Invalida el cooldown para forzar que el próximo `getListAsync()` vuelva
   *  a pedir delta al server. */
  clearCache(): void {
    serviceSync.reset(`grpc:aplicaciones:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:aplicaciones:${this._fincaId}`, async () => {
      const ultimaSincronizacion = await grpcAplicacionDatabase.getUltimaSincronizacionAsync(
        this._fincaId,
      );
      const client = createClient(AplicacionService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcAplicacionDatabase.putDeltaAsync(
        this._fincaId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createAplicacionGetListByFincaIdService`). */
export const fx_createAplicacionGrpcService = createCachedFactory(
  (fincaId: string) => new AplicacionGrpcService(fincaId),
);

/** Adapta un `AplicacionItem` (gRPC) al shape `AplicacionResponse` (REST) —
 *  ver el comentario de la clase. Exportada porque los servicios de
 *  guardado/borrado también la necesitan. */
export function toAplicacionResponse(
  aplicacion: AplicacionItem,
): AplicacionResponse {
  return {
    id: aplicacion.id,
    isEliminado: aplicacion.eliminado,
    clienteId: "",
    fincaId: aplicacion.fincaId,
    cultivoId: null,
    cultivoTratamientoId: null,
    cultivoNombre: aplicacion.cultivoNombre ?? null,
    cultivoTratamientoNombre: aplicacion.cultivoTratamientoNombre ?? null,
    fechaAplicacion: aplicacion.fechaAplicacion,
    metodo: (aplicacion.metodo || "dron") as MetodoAplicacion,
    // Las aplicaciones manuales no tienen vuelo y el campo llega vacío: `JSON.parse("")`
    // tiraría, así que el null se resuelve acá y no en cada consumidor.
    vuelo: aplicacion.vuelo ? JSON.parse(aplicacion.vuelo) : null,
    taskArea: aplicacion.taskArea,
    totalAmount: aplicacion.totalAmount,
    recipienteId: aplicacion.recipienteId ?? null,
    recipienteNombre: aplicacion.recipienteNombre ?? null,
    volumenRecipienteLitros: aplicacion.volumenRecipienteLitros ?? null,
    mezclaPorPlantaMl: aplicacion.mezclaPorPlantaMl ?? null,
    capacidadBombaLitros: aplicacion.capacidadBombaLitros ?? null,
    equipo: aplicacion.equipo ?? null,
    responsable: aplicacion.responsable ?? null,
    // Mismo criterio que los detalles: el lote de una aplicación nunca se sincroniza
    // suelto, así que no tiene id propio y se rellena con el loteId.
    lotes: aplicacion.lotes.map((lote) => ({
      id: lote.loteId,
      isEliminado: false,
      loteId: lote.loteId,
      loteNombre: lote.loteNombre,
      cantidad: lote.cantidad ?? null,
      plantas: lote.plantas ?? null,
    })),
    // AplicacionDetalleItem no tiene id propio (nunca se sincroniza suelto, siempre
    // embebido en la aplicación) — se rellena con productoId, inerte: nada lee
    // detalle.id/isEliminado (confirmado en toda la app antes de recortarlo del proto).
    detalles: aplicacion.detalles.map((detalle) => ({
      id: detalle.productoId,
      isEliminado: false,
      productoId: detalle.productoId,
      productoDescripcion: detalle.productoDescripcion,
      dosis: detalle.dosis,
      unidadMedida: detalle.unidadMedida,
    })),
  };
}
